/**
 * EditDataTemplateForm
 * Reusable form for editing a single DataTemplate record.
 * Fetches by dataTemplateID, shows metadata fields + JSON contentData editor.
 *
 * Props:
 *   dataTemplateID  – ID of the record to load and edit
 *   onSaved         – optional callback fired after a successful save
 *   readOnly        – render in read-only mode (no save button)
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { DataTemplateApiClient } from '../ApiClient/DataTemplateApiClient';
import type { DataTemplateRecord, DataTemplateMetaEdits } from '../types';

// JsonEditor injection — callers inside the designer package provide it via the jsonEditor prop.
// When this module moves to its own package, pass in whatever editor is available.

// ─── Helpers ─────────────────────────────────────────────────────────────────

function prettyJson(raw: string | null | undefined): string {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw ?? ''; }
}

function metaFrom(record: DataTemplateRecord): DataTemplateMetaEdits {
  return { templateName: record.templateName, displayOrder: record.displayOrder, enabled: record.enabled };
}

// ─── Component ───────────────────────────────────────────────────────────────

export interface EditDataTemplateFormProps {
  dataTemplateID: number;
  /** Fired after the record is saved successfully */
  onSaved?: (record: DataTemplateRecord) => void;
  readOnly?: boolean;
  /**
   * Optional rich JSON editor component to render for contentData.
   * When omitted, a plain <textarea> is used.
   * Pass the JsonEditor from @flow-studio/designer or any compatible component.
   * Expected props: value, onChange, readOnly, fill, showFormatButton, showCopyButton, showTreeView, showValidationStatus
   */
  JsonEditor?: React.ComponentType<any>;
}

interface FormState {
  record: DataTemplateRecord | null;
  json: string;
  metaEdits: DataTemplateMetaEdits | null;
  loading: boolean;
  loadError: string | null;
  saving: boolean;
  saved: boolean;
  saveError: string | null;
}

const apiClient = new DataTemplateApiClient();

export const EditDataTemplateForm: React.FC<EditDataTemplateFormProps> = ({
  dataTemplateID,
  onSaved,
  readOnly = false,
  JsonEditor,
}) => {
  const [state, setState] = useState<FormState>({
    record: null, json: '', metaEdits: null,
    loading: true, loadError: null,
    saving: false, saved: false, saveError: null,
  });
  const abortRef = useRef<AbortController | null>(null);

  const patch = (delta: Partial<FormState>) => setState(prev => ({ ...prev, ...delta }));

  // Load record
  useEffect(() => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    patch({ loading: true, loadError: null, record: null, json: '', metaEdits: null, saved: false, saveError: null });

    (async () => {
      const record = await apiClient.getById(dataTemplateID);
      if (ctrl.signal.aborted) return;

      if (!record) {
        patch({ loading: false, loadError: `DataTemplate ID ${dataTemplateID} not found.` });
        return;
      }
      patch({ loading: false, record, json: prettyJson(record.contentData), metaEdits: metaFrom(record) });
    })();

    return () => { ctrl.abort(); };
  }, [dataTemplateID]);

  // Derive tenant flags from current ContentData JSON — stays in sync with the JSON editor
  const tenantFlags = useMemo(() => {
    try {
      const parsed = JSON.parse(state.json || '{}');
      return {
        isFrequent: !!parsed.isFrequent,
        isFavorite: !!parsed.isFavorite,
        isTopRated: !!parsed.isTopRated,
      };
    } catch {
      return { isFrequent: false, isFavorite: false, isTopRated: false };
    }
  }, [state.json]);

  const setTenantFlag = useCallback((key: 'isFrequent' | 'isFavorite' | 'isTopRated', value: boolean) => {
    try {
      const parsed = JSON.parse(state.json || '{}');
      parsed[key] = value;
      patch({ json: JSON.stringify(parsed, null, 2) });
    } catch {
      // JSON is currently invalid — ignore; user must fix JSON first
    }
  }, [state.json]);

  const handleSave = async () => {
    const { record, json, metaEdits } = state;
    if (!record || !metaEdits) return;

    patch({ saving: true, saveError: null, saved: false });
    const result = await apiClient.update(record, json, metaEdits);

    if (result.success) {
      const updated = { ...record, contentData: json, ...metaEdits };
      patch({ saving: false, saved: true, record: updated });
      onSaved?.(updated);
      setTimeout(() => patch({ saved: false }), 2500);
    } else {
      patch({ saving: false, saveError: result.error ?? 'Save failed.' });
    }
  };

  const { record, json, metaEdits, loading, loadError, saving, saved, saveError } = state;

  if (loading) {
    return (
      <div style={centerStyle}>
        <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '13px' }}>Loading…</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={errorStyle}>{loadError}</div>
    );
  }

  if (!record || !metaEdits) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: '0' }}>

      {/* ── Split: metadata left | JSON right ── */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: '16px', overflow: 'hidden' }}>

        {/* Metadata panel */}
        <div style={metaPanelStyle}>
          <SectionLabel>Record Info</SectionLabel>

          <FieldGroup label="DataTemplate ID">
            <IdBadge>{record.dataTemplateID}</IdBadge>
          </FieldGroup>

          {record.dataTemplateCategoryID != null && (
            <FieldGroup label="Category ID">
              <IdBadge>{record.dataTemplateCategoryID}</IdBadge>
            </FieldGroup>
          )}
          {record.dataTemplateTypeID != null && (
            <FieldGroup label="Type ID">
              <IdBadge>{record.dataTemplateTypeID}</IdBadge>
            </FieldGroup>
          )}

          <SectionLabel style={{ marginTop: '12px' }}>Editable Fields</SectionLabel>

          <FieldGroup label="Template Name">
            <input
              type="text"
              value={metaEdits.templateName}
              disabled={readOnly}
              onChange={e => patch({ metaEdits: { ...metaEdits, templateName: e.target.value } })}
              style={{ ...inputStyle, opacity: readOnly ? 0.5 : 1 }}
            />
          </FieldGroup>

          <FieldGroup label="Display Order">
            <input
              type="number"
              value={metaEdits.displayOrder}
              disabled={readOnly}
              onChange={e => patch({ metaEdits: { ...metaEdits, displayOrder: parseInt(e.target.value, 10) || 0 } })}
              style={{ ...inputStyle, width: '80px', opacity: readOnly ? 0.5 : 1 }}
            />
          </FieldGroup>

          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: readOnly ? 'default' : 'pointer' }}>
            <input
              type="checkbox"
              checked={metaEdits.enabled}
              disabled={readOnly}
              onChange={e => patch({ metaEdits: { ...metaEdits, enabled: e.target.checked } })}
              style={{ accentColor: '#309B48', width: '14px', height: '14px' }}
            />
            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>Enabled</span>
          </label>

          <SectionLabel style={{ marginTop: '12px' }}>Flags</SectionLabel>

          {([
            ['Is Template', record.isTemplate],
            ['Is System', record.isSystem],
            ['Published', record.published],
            ['Public', record.isPublic],
            ['Global', record.isGlobal],
            ['Has Wildcards', record.hasWildCards],
          ] as [string, boolean][]).map(([label, val]) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
              <span style={{
                width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0,
                background: val ? '#22c55e' : '#374151',
                border: '1px solid rgba(255,255,255,0.12)',
              }} />
              <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{label}</span>
            </div>
          ))}

          <SectionLabel style={{ marginTop: '16px' }}>Tenant Curation</SectionLabel>
          <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.22)', marginTop: '-6px', lineHeight: 1.4 }}>
            Stored in ContentData — tenant-specific
          </span>

          {([
            ['isFrequent', 'Frequent'],
            ['isFavorite', 'Favorite'],
            ['isTopRated', 'Top Rated'],
          ] as ['isFrequent' | 'isFavorite' | 'isTopRated', string][]).map(([key, label]) => (
            <label
              key={key}
              style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: readOnly ? 'default' : 'pointer' }}
            >
              <input
                type="checkbox"
                checked={tenantFlags[key]}
                disabled={readOnly}
                onChange={e => setTenantFlag(key, e.target.checked)}
                style={{ accentColor: '#309B48', width: '14px', height: '14px' }}
              />
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)' }}>{label}</span>
            </label>
          ))}
        </div>

        {/* Divider */}
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

        {/* JSON editor */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <SectionLabel>Content Data (JSON)</SectionLabel>
          <div style={{ flex: 1, minHeight: 0, marginTop: '8px' }}>
            {JsonEditor ? (
              <JsonEditor
                value={json}
                onChange={(v: string) => patch({ json: v })}
                readOnly={readOnly}
                placeholder="Template JSON…"
                showFormatButton
                showCopyButton
                showTreeView
                showValidationStatus
                fill
              />
            ) : (
              <textarea
                value={json}
                readOnly={readOnly}
                onChange={e => patch({ json: e.target.value })}
                spellCheck={false}
                style={{
                  width: '100%', height: '100%', boxSizing: 'border-box',
                  background: 'rgba(0,0,0,0.3)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: '#e2e8f0',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  padding: '10px',
                  resize: 'none',
                  outline: 'none',
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      {!readOnly && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', paddingTop: '12px' }}>
          {saved && <span style={{ fontSize: '12px', color: '#22c55e' }}>✓ Saved</span>}
          {saveError && <span style={{ fontSize: '12px', color: '#ef4444' }}>{saveError}</span>}
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '7px 22px', borderRadius: '6px', border: 'none',
              background: saving ? '#374151' : '#309B48',
              color: '#fff', fontSize: '12px', fontWeight: 600,
              cursor: saving ? 'not-allowed' : 'pointer',
              transition: 'background 0.15s',
            }}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Style helpers ────────────────────────────────────────────────────────────

const centerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%',
};

const errorStyle: React.CSSProperties = {
  background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
  borderRadius: '8px', padding: '16px', color: '#fca5a5', fontSize: '13px',
};

const metaPanelStyle: React.CSSProperties = {
  width: '220px', flexShrink: 0, display: 'flex', flexDirection: 'column',
  gap: '10px', overflowY: 'auto', paddingBottom: '8px',
};

const inputStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: '5px',
  color: '#e2e8f0',
  fontSize: '12px',
  padding: '5px 8px',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
};

const SectionLabel: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <span style={{
    fontSize: '10px', color: 'rgba(255,255,255,0.35)',
    textTransform: 'uppercase', letterSpacing: '0.07em', ...style,
  }}>
    {children}
  </span>
);

const FieldGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <span style={{ fontSize: '10px', color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {label}
    </span>
    {children}
  </div>
);

const IdBadge: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span style={{
    display: 'inline-block', background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(255,255,255,0.1)', borderRadius: '4px',
    padding: '3px 7px', fontSize: '11px', color: 'rgba(255,255,255,0.4)',
    fontFamily: 'monospace',
  }}>
    {children}
  </span>
);
